import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function LoginModal({ isOpen, onClose, setUser }) {
    const [mode, setMode] = useState("login");
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    if (!isOpen) return null;

    // ============================================
    // HANDLE INPUT CHANGE
    // ============================================
    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });

        setErrors({
            ...errors,
            [e.target.name]: "",
            general: "",
        });

        setSuccess("");
    };

    // ============================================
    // VALIDATION
    // ============================================
    const validate = () => {
        let newErrors = {};

        // Name validation for registration
        if (mode === "register" && !form.name.trim()) {
            newErrors.name = "Full name is required";
        }

        // Email validation
        if (!form.email.trim()) {
            newErrors.email = "Email is required";
        }

        // Password is not required for forgot password
        if (mode !== "forgot" && !form.password.trim()) {
            newErrors.password = "Password is required";
        }

        return newErrors;
    };

    // ============================================
    // HANDLE SUBMIT
    // ============================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrors({});
        setSuccess("");

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            // ========================================
            // FORGOT PASSWORD
            // ========================================
            if (mode === "forgot") {
                await axios.post(
                    "https://beachhotelsdubai.com/api/forgot-password",
                    {
                        email: form.email,
                    }
                );

                setSuccess(
                    "If an account exists with this email, a password reset link has been sent."
                );

                setLoading(false);

                return;
            }

            // ========================================
            // REGISTER
            // ========================================
            if (mode === "register") {
                await axios.post(
                    "https://beachhotelsdubai.com/api/register",
                    {
                        name: form.name,
                        email: form.email,
                        password: form.password,
                        password_confirmation: form.password,
                    }
                );

                alert("User registered successfully");

                setForm({
                    name: "",
                    email: "",
                    password: "",
                });

                setMode("login");
                setErrors({});
                setSuccess("");

                setLoading(false);

                return;
            }

            // ========================================
            // LOGIN
            // ========================================
            if (mode === "login") {
                const res = await axios.post(
                    "https://beachhotelsdubai.com/api/login",
                    {
                        email: form.email,
                        password: form.password,
                    }
                );

                const userData = res.data.user;

                // Save user
                localStorage.setItem(
                    "user",
                    JSON.stringify(userData)
                );

                // Update user state
                setUser(userData);

                alert("Login successful");

                setLoading(false);

                // Close modal
                onClose();

                // Redirect to profile
                navigate("/profile");
            }
        } catch (err) {
            console.error(err);

            setLoading(false);

            // Forgot password error
            if (mode === "forgot") {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Unable to send the reset link. Please try again later.",
                });

                return;
            }

            // Login / register error
            setErrors({
                general:
                    err.response?.data?.message ||
                    "Authentication failed. Please check your details.",
            });
        }
    };

    // ============================================
    // GO TO LOGIN
    // ============================================
    const goToLogin = () => {
        setMode("login");

        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    // ============================================
    // GO TO REGISTER
    // ============================================
    const goToRegister = () => {
        setMode("register");

        setErrors({});
        setSuccess("");
    };

    // ============================================
    // GO TO FORGOT PASSWORD
    // ============================================
    const goToForgotPassword = () => {
        setMode("forgot");

        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 px-4">

            {/* ============================================
                MODAL
            ============================================ */}
            <div className="relative bg-white w-full max-w-md rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] p-6 sm:p-8 border border-[#0F172A]/20">

                {/* ============================================
                    CLOSE BUTTON
                ============================================ */}
                <button
                    onClick={onClose}
                    type="button"
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-orange-50 text-gray-500 hover:text-[#FF8C42] transition"
                >
                    ✕
                </button>


                {/* ============================================
                    TITLE
                ============================================ */}
                <h2 className="text-2xl font-bold text-center mb-2 bg-gradient-to-r from-[#0F172A] to-[#FF8C42] bg-clip-text text-transparent">

                    {mode === "register"
                        ? "Create Account"
                        : mode === "forgot"
                            ? "Forgot Password"
                            : "Sign In"}

                </h2>


                {/* ============================================
                    SUBTITLE
                ============================================ */}
                <p className="text-center text-gray-500 text-sm mb-6">

                    {mode === "register"
                        ? "Create your account to manage your bookings"
                        : mode === "forgot"
                            ? "Enter your email and we'll send you a reset link"
                            : "Login to manage your hotel bookings"}

                </p>


                {/* ============================================
                    GENERAL ERROR
                ============================================ */}
                {errors.general && (
                    <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                        {errors.general}
                    </div>
                )}


                {/* ============================================
                    SUCCESS MESSAGE
                ============================================ */}
                {success && (
                    <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
                        {success}
                    </div>
                )}


                {/* ============================================
                    FORM
                ============================================ */}
                <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                >

                    {/* ========================================
                        NAME
                    ======================================== */}
                    {mode === "register" && (
                        <>
                            <input
                                name="name"
                                value={form.name}
                                placeholder="Full Name"
                                onChange={handleChange}
                                className="w-full border border-gray-300 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF8C42] focus:border-[#FF8C42] transition"
                            />

                            {errors.name && (
                                <p className="text-red-500 text-sm -mt-2">
                                    {errors.name}
                                </p>
                            )}
                        </>
                    )}


                    {/* ========================================
                        EMAIL
                    ======================================== */}
                    <input
                        name="email"
                        type="email"
                        value={form.email}
                        placeholder="Email"
                        onChange={handleChange}
                        className="w-full border border-gray-300 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF8C42] focus:border-[#FF8C42] transition"
                    />

                    {errors.email && (
                        <p className="text-red-500 text-sm -mt-2">
                            {errors.email}
                        </p>
                    )}


                    {/* ========================================
                        PASSWORD
                    ======================================== */}
                    {mode !== "forgot" && (
                        <>
                            <input
                                name="password"
                                type="password"
                                value={form.password}
                                placeholder="Password"
                                onChange={handleChange}
                                className="w-full border border-gray-300 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF8C42] focus:border-[#FF8C42] transition"
                            />

                            {errors.password && (
                                <p className="text-red-500 text-sm -mt-2">
                                    {errors.password}
                                </p>
                            )}


                            {/* ====================================
                                FORGOT PASSWORD
                            ==================================== */}
                            {mode === "login" && (
                                <div className="flex justify-end -mt-1">

                                    <button
                                        type="button"
                                        onClick={goToForgotPassword}
                                        className="text-sm font-medium text-[#0F172A] hover:text-[#FF8C42] transition"
                                    >
                                        Forgot Password?
                                    </button>

                                </div>
                            )}
                        </>
                    )}


                    {/* ========================================
                        SUBMIT BUTTON
                    ======================================== */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full px-4 py-3 bg-gradient-to-r from-[#0F172A] to-[#FF8C42] text-white rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                        {loading
                            ? "Please wait..."
                            : mode === "register"
                                ? "Register"
                                : mode === "forgot"
                                    ? "Send Reset Link"
                                    : "Login"}
                    </button>

                </form>


                {/* ============================================
                    BOTTOM NAVIGATION
                ============================================ */}
                <div className="text-center mt-5 text-sm">

                    {/* LOGIN */}
                    {mode === "login" && (
                        <button
                            type="button"
                            onClick={goToRegister}
                            className="text-[#0F172A] font-semibold hover:text-[#FF8C42] transition"
                        >
                            Create account
                        </button>
                    )}


                    {/* REGISTER */}
                    {mode === "register" && (
                        <button
                            type="button"
                            onClick={goToLogin}
                            className="text-[#0F172A] font-semibold hover:text-[#FF8C42] transition"
                        >
                            Back to login
                        </button>
                    )}


                    {/* FORGOT PASSWORD */}
                    {mode === "forgot" && (
                        <button
                            type="button"
                            onClick={goToLogin}
                            className="text-[#0F172A] font-semibold hover:text-[#FF8C42] transition"
                        >
                            ← Back to login
                        </button>
                    )}

                </div>

            </div>
        </div>
    );
}